/**
 * AudioWaveform Component
 * A minimal, elegant audio visualization that pulses when audio is playing.
 * Shows users that audio is active and synced with the ritual.
 */

import { motion } from "framer-motion";

const AudioWaveform = ({ 
  isPlaying = false, 
  barCount = 5, 
  color = "currentColor",
  size = "md" // sm, md, lg
}) => {
  const sizes = {
    sm: { height: 16, barWidth: 2, gap: 2 },
    md: { height: 24, barWidth: 3, gap: 3 },
    lg: { height: 32, barWidth: 4, gap: 4 }
  };
  
  const { height, barWidth, gap } = sizes[size] || sizes.md;
  const totalWidth = barCount * barWidth + (barCount - 1) * gap;
  
  // Different animation patterns for each bar to create organic feel
  const barPatterns = [
    { heights: [0.3, 0.8, 0.5, 1, 0.4], duration: 0.8 },
    { heights: [0.5, 1, 0.3, 0.7, 0.9], duration: 0.7 },
    { heights: [0.4, 0.6, 1, 0.5, 0.3], duration: 0.9 },
    { heights: [0.7, 0.4, 0.8, 0.3, 1], duration: 0.75 },
    { heights: [0.6, 0.9, 0.4, 0.8, 0.5], duration: 0.85 },
    { heights: [0.3, 0.7, 0.9, 0.4, 0.6], duration: 0.8 },
    { heights: [0.8, 0.5, 0.3, 1, 0.7], duration: 0.7 },
  ];

  return (
    <div 
      className="flex items-center justify-center"
      style={{ width: totalWidth, height }}
      data-testid="audio-waveform"
    >
      {Array.from({ length: barCount }).map((_, index) => {
        const pattern = barPatterns[index % barPatterns.length];
        
        return (
          <motion.div
            key={index}
            className="rounded-full"
            style={{
              width: barWidth,
              backgroundColor: color,
              marginLeft: index > 0 ? gap : 0,
            }}
            initial={{ height: height * 0.2 }}
            animate={isPlaying ? {
              height: pattern.heights.map(h => height * h),
              opacity: [0.6, 1, 0.8, 1, 0.6]
            } : {
              height: height * 0.2,
              opacity: 0.4
            }}
            transition={isPlaying ? {
              duration: pattern.duration,
              repeat: Infinity,
              ease: "easeInOut",
              delay: index * 0.1
            } : {
              duration: 0.3
            }}
          />
        );
      })}
    </div>
  );
};

export default AudioWaveform;
